/**
 * GraphQL Mutation definitions
 * 
 * These mutations match the AppSync GraphQL schema defined in infrastructure/graphql/schema.graphql
 */

export const createApplicant = /* GraphQL */ `
  mutation CreateApplicant($input: CreateApplicantInput!) {
    createApplicant(input: $input) {
      id
      email
      fullName
      homePhone
      mobilePhone
      createdAt
      updatedAt
    }
  }
`;

export const createApplicantYearProfile = /* GraphQL */ `
  mutation CreateApplicantYearProfile($input: CreateApplicantYearProfileInput!) {
    createApplicantYearProfile(input: $input) {
      applicantId
      competitionYear
      applicantType
      submissionStatus
      createdAt
      updatedAt
    }
  }
`;

export const updateApplicantYearProfile = /* GraphQL */ `
  mutation UpdateApplicantYearProfile($input: UpdateApplicantYearProfileInput!) {
    updateApplicantYearProfile(input: $input) {
      applicantId
      competitionYear
      applicantType
      submissionStatus
      phase1Data
      stageFormData
      interviewInfoData
      placementQuestionsData
      placementQuestionsLocked
      profileLocked
      outstandingFees
      digitalSignature
      signedAt
      directorNotes
      createdAt
      updatedAt
    }
  }
`;

export const submitPhase1 = /* GraphQL */ `
  mutation SubmitPhase1($applicantId: ID!, $competitionYear: Int!) {
    submitPhase1(applicantId: $applicantId, competitionYear: $competitionYear) {
      applicantId
      competitionYear
      submissionStatus
      updatedAt
    }
  }
`;

export const submitPlacementQuestions = /* GraphQL */ `
  mutation SubmitPlacementQuestions($applicantId: ID!, $competitionYear: Int!) {
    submitPlacementQuestions(applicantId: $applicantId, competitionYear: $competitionYear) {
      applicantId
      competitionYear
      placementQuestionsLocked
      updatedAt
    }
  }
`;

export const approveApplication = /* GraphQL */ `
  mutation ApproveApplication($applicantId: ID!, $competitionYear: Int!) {
    approveApplication(applicantId: $applicantId, competitionYear: $competitionYear) {
      applicantId
      competitionYear
      submissionStatus
      updatedAt
    }
  }
`;

// Post-approval edit of the contestant approval facets (merit-implementation
// task 5.2, Req 2.1, 2.3). The contestant approvals flow is the SINGLE director
// surface that sets approvedTitle / divisionKey: approveApplication sets them at
// approval time and this mutation is that same surface's post-approval EDIT
// action. It routes through the SAME single writer + validation; an invalid
// division or empty/over-length title is rejected with the prior value left
// unchanged (Req 2.5, 2.6).
export const editApprovedParticipationFacets = /* GraphQL */ `
  mutation EditApprovedParticipationFacets(
    $applicantId: ID!
    $competitionYear: Int!
    $approvedTitle: String
    $divisionKey: String
  ) {
    editApprovedParticipationFacets(
      applicantId: $applicantId
      competitionYear: $competitionYear
      approvedTitle: $approvedTitle
      divisionKey: $divisionKey
    ) {
      participationId
      personId
      competitionYear
      approvedTitle
      divisionKey
    }
  }
`;

export const rejectApplication = /* GraphQL */ `
  mutation RejectApplication($applicantId: ID!, $competitionYear: Int!, $note: String!) {
    rejectApplication(applicantId: $applicantId, competitionYear: $competitionYear, note: $note) {
      applicantId
      competitionYear
      submissionStatus
      directorNotes
      updatedAt
    }
  }
`;

export const lockProfile = /* GraphQL */ `
  mutation LockProfile($applicantId: ID!, $competitionYear: Int!) {
    lockProfile(applicantId: $applicantId, competitionYear: $competitionYear) {
      applicantId
      competitionYear
      profileLocked
      updatedAt
    }
  }
`;

export const setOutstandingFees = /* GraphQL */ `
  mutation SetOutstandingFees($applicantId: ID!, $competitionYear: Int!, $hasOutstandingFees: Boolean!) {
    setOutstandingFees(applicantId: $applicantId, competitionYear: $competitionYear, hasOutstandingFees: $hasOutstandingFees) {
      applicantId
      competitionYear
      outstandingFees
      updatedAt
    }
  }
`;

export const addDirectorNote = /* GraphQL */ `
  mutation AddDirectorNote($applicantId: ID!, $competitionYear: Int!, $note: String!) {
    addDirectorNote(applicantId: $applicantId, competitionYear: $competitionYear, note: $note) {
      applicantId
      competitionYear
      directorNotes
      updatedAt
    }
  }
`;

export const createVolunteerActivity = /* GraphQL */ `
  mutation CreateVolunteerActivity($input: CreateVolunteerActivityInput!) {
    createVolunteerActivity(input: $input) {
      id
      applicantId
      organizationName
      activityDate
      hoursContributed
      description
      verifiedByDirector
      createdAt
    }
  }
`;

export const updateVolunteerActivity = /* GraphQL */ `
  mutation UpdateVolunteerActivity($input: UpdateVolunteerActivityInput!) {
    updateVolunteerActivity(input: $input) {
      id
      applicantId
      organizationName
      activityDate
      hoursContributed
      description
      verifiedByDirector
    }
  }
`;

export const deleteVolunteerActivity = /* GraphQL */ `
  mutation DeleteVolunteerActivity($id: ID!) {
    deleteVolunteerActivity(id: $id)
  }
`;

export const verifyVolunteerActivity = /* GraphQL */ `
  mutation VerifyVolunteerActivity($id: ID!) {
    verifyVolunteerActivity(id: $id) {
      id
      verifiedByDirector
    }
  }
`;

export const getDocumentUploadUrl = /* GraphQL */ `
  mutation GetDocumentUploadUrl($input: GetUploadUrlInput!) {
    getDocumentUploadUrl(input: $input) {
      uploadUrl
      s3Key
      expiresAt
    }
  }
`;

export const createDocumentReference = /* GraphQL */ `
  mutation CreateDocumentReference($input: CreateDocumentReferenceInput!) {
    createDocumentReference(input: $input) {
      id
      applicantId
      competitionYear
      documentType
      s3Key
      fileName
      contentType
      uploadedAt
    }
  }
`;

export const deleteDocumentReference = /* GraphQL */ `
  mutation DeleteDocumentReference($id: ID!) {
    deleteDocumentReference(id: $id)
  }
`;

export const updateBrandingConfig = /* GraphQL */ `
  mutation UpdateBrandingConfig($input: UpdateBrandingConfigInput!) {
    updateBrandingConfig(input: $input) {
      competitionYear
      pageantName
      logoS3Key
      primaryColor
      accentColor
      headerText
      updatedAt
    }
  }
`;

export const uploadBrandingLogo = /* GraphQL */ `
  mutation UploadBrandingLogo($competitionYear: Int!) {
    uploadBrandingLogo(competitionYear: $competitionYear) {
      uploadUrl
      s3Key
      expiresAt
    }
  }
`;

export const createFormSchema = /* GraphQL */ `
  mutation CreateFormSchema($input: CreateFormSchemaInput!) {
    createFormSchema(input: $input) {
      id
      formType
      applicantType
      competitionYear
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const updateFormSchema = /* GraphQL */ `
  mutation UpdateFormSchema($input: UpdateFormSchemaInput!) {
    updateFormSchema(input: $input) {
      id
      formType
      applicantType
      competitionYear
      isActive
      updatedAt
    }
  }
`;

export const createFormField = /* GraphQL */ `
  mutation CreateFormField($input: CreateFormFieldInput!) {
    createFormField(input: $input) {
      id
      formSchemaId
      label
      fieldType
      required
      wordLimit
      characterLimit
      options
      displayOrder
      immutableOnSubmit
      thirdPersonRequired
      applicantTypeVisibility
    }
  }
`;

export const updateFormField = /* GraphQL */ `
  mutation UpdateFormField($input: UpdateFormFieldInput!) {
    updateFormField(input: $input) {
      id
      formSchemaId
      label
      fieldType
      required
      wordLimit
      characterLimit
      options
      displayOrder
      immutableOnSubmit
      thirdPersonRequired
      applicantTypeVisibility
    }
  }
`;

export const deleteFormField = /* GraphQL */ `
  mutation DeleteFormField($id: ID!) {
    deleteFormField(id: $id)
  }
`;

export const createAdminUser = /* GraphQL */ `
  mutation CreateAdminUser($email: String!, $firstName: String!, $lastName: String!, $groupName: String) {
    createAdminUser(email: $email, firstName: $firstName, lastName: $lastName, groupName: $groupName) {
      email
      temporaryPassword
      success
      message
    }
  }
`;

export const sendWelcomeEmail = /* GraphQL */ `
  mutation SendWelcomeEmail($email: String!, $fullName: String!, $temporaryPassword: String, $role: String) {
    sendWelcomeEmail(email: $email, fullName: $fullName, temporaryPassword: $temporaryPassword, role: $role) {
      email
      success
      message
    }
  }
`;

export const deleteApplicant = /* GraphQL */ `
  mutation DeleteApplicant($id: ID!) {
    deleteApplicant(id: $id)
  }
`;

export const deleteApplicantYearProfile = /* GraphQL */ `
  mutation DeleteApplicantYearProfile($applicantId: ID!, $competitionYear: Int!) {
    deleteApplicantYearProfile(applicantId: $applicantId, competitionYear: $competitionYear)
  }
`;

// Profile photo mutations
// Requirements: 2.3, 2.4, 2.9, 3.2, 3.4

export const getProfilePhotoUploadUrl = /* GraphQL */ `
  mutation GetProfilePhotoUploadUrl($userId: String!, $contentType: String!) {
    getProfilePhotoUploadUrl(userId: $userId, contentType: $contentType) {
      uploadUrl
      s3Key
      expiresIn
    }
  }
`;

export const updateProfilePhoto = /* GraphQL */ `
  mutation UpdateProfilePhoto($input: UpdateProfilePhotoInput!) {
    updateProfilePhoto(input: $input)
  }
`;

export const removeProfilePhoto = /* GraphQL */ `
  mutation RemoveProfilePhoto {
    removeProfilePhoto
  }
`;

// Crop profile photo mutation (avatar-crop feature)
// Requirements: 5.1, 6.1, 8.1, 8.2, 8.3
export const cropProfilePhoto = /* GraphQL */ `
  mutation CropProfilePhoto($input: CropProfilePhotoInput!) {
    cropProfilePhoto(input: $input) {
      croppedPhotoS3Key
    }
  }
`;

export const deleteCognitoUser = /* GraphQL */ `
  mutation DeleteCognitoUser($email: String!) {
    deleteCognitoUser(email: $email) {
      success
      message
    }
  }
`;

export const changeUserRole = /* GraphQL */ `
  mutation ChangeUserRole($email: String!, $oldGroupName: String!, $newGroupName: String!) {
    changeUserRole(email: $email, oldGroupName: $oldGroupName, newGroupName: $newGroupName) {
      email
      success
      message
    }
  }
`;

export const setAccessHats = /* GraphQL */ `
  mutation SetAccessHats($email: String!, $hats: [String!]!, $portalAccess: Boolean) {
    setAccessHats(email: $email, hats: $hats, portalAccess: $portalAccess) {
      email
      success
      message
    }
  }
`;

export const updateDirectorProfilePhoto = /* GraphQL */ `
  mutation UpdateDirectorProfilePhoto($email: String!, $profilePhotoS3Key: String) {
    updateDirectorProfilePhoto(email: $email, profilePhotoS3Key: $profilePhotoS3Key) {
      success
      message
    }
  }
`;

export const createInquiry = /* GraphQL */ `
  mutation CreateInquiry($input: CreateInquiryInput!) {
    createInquiry(input: $input) {
      id
      name
      email
      subject
      message
      status
      createdAt
      updatedAt
    }
  }
`;

// Update inquiry status mutation (admin only, Cognito auth)
// Requirements: 7.5, 8.5, 10.6
export const updateInquiryStatus = /* GraphQL */ `
  mutation UpdateInquiryStatus($input: UpdateInquiryStatusInput!) {
    updateInquiryStatus(input: $input) {
      id
      name
      email
      subject
      message
      status
      createdAt
      updatedAt
    }
  }
`;

// Delete inquiry mutation (admin only, Cognito auth)
export const deleteInquiry = /* GraphQL */ `
  mutation DeleteInquiry($id: ID!) {
    deleteInquiry(id: $id) {
      id
    }
  }
`;

// Email template mutation (admin only, Cognito auth)
// Requirements: 1.5
export const updateEmailTemplate = /* GraphQL */ `
  mutation UpdateEmailTemplate($input: UpdateEmailTemplateInput!) {
    updateEmailTemplate(input: $input) {
      id
      templateName
      subject
      body
      createdAt
      updatedAt
      lastEditedBy
    }
  }
`;

// Send inquiry response mutation (admin only, Cognito auth)
// Requirements: 1.3, 7.3
export const sendInquiryResponse = /* GraphQL */ `
  mutation SendInquiryResponse($input: SendInquiryResponseInput!) {
    sendInquiryResponse(input: $input) {
      id
      inquiryId
      adminEmail
      adminName
      responseText
      createdAt
      updatedAt
    }
  }
`;

// System log mutations (admin only, Cognito auth)
// Audit logging for admin actions
export const createSystemLogEntry = /* GraphQL */ `
  mutation CreateSystemLogEntry($input: CreateSystemLogEntryInput!) {
    createSystemLogEntry(input: $input) {
      id
      timestamp
      action
      performedBy
      targetName
      targetEmail
      targetId
      details
    }
  }
`;

export const deleteSystemLogEntry = /* GraphQL */ `
  mutation DeleteSystemLogEntry($id: ID!) {
    deleteSystemLogEntry(id: $id)
  }
`;

export const createCalendarEvent = /* GraphQL */ `
  mutation CreateCalendarEvent($input: CreateCalendarEventInput!) {
    createCalendarEvent(input: $input) {
      id
      title
      date
      time
      endDate
      endTime
      allDay
      location
      description
      type
      competitionYear
      isCountdownAnchor
      createdBy
      createdAt
      updatedAt
    }
  }
`;

export const updateCalendarEvent = /* GraphQL */ `
  mutation UpdateCalendarEvent($input: UpdateCalendarEventInput!) {
    updateCalendarEvent(input: $input) {
      id
      title
      date
      time
      endDate
      endTime
      allDay
      location
      description
      type
      competitionYear
      isCountdownAnchor
      createdBy
      createdAt
      updatedAt
    }
  }
`;

export const deleteCalendarEvent = /* GraphQL */ `
  mutation DeleteCalendarEvent($id: ID!) {
    deleteCalendarEvent(id: $id)
  }
`;

// User mutations (unified user identity)
// Requirements: 8.5
export const updateUser = /* GraphQL */ `
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      cognitoSub
      email
      fullName
      role
      profilePhotoS3Key
      createdAt
      updatedAt
      lastLoginAt
    }
  }
`;

// Channel read tracking mutation — REMOVED by stream-chat-messaging-migration.
// Stream Chat's `channel.markRead()` API handles read state natively.

// ========================================
// Payment Mutations
// ========================================

export const createFee = /* GraphQL */ `
  mutation CreateFee($input: CreateFeeInput!) {
    createFee(input: $input) {
      feeId
      competitionYear
      name
      description
      amount
      statusTrigger
      required
      dueDate
      active
      createdAt
      updatedAt
    }
  }
`;

export const updateFee = /* GraphQL */ `
  mutation UpdateFee($input: UpdateFeeInput!) {
    updateFee(input: $input) {
      feeId
      competitionYear
      name
      description
      amount
      statusTrigger
      required
      dueDate
      active
      createdAt
      updatedAt
    }
  }
`;

export const createDiscountRule = /* GraphQL */ `
  mutation CreateDiscountRule($input: CreateDiscountRuleInput!) {
    createDiscountRule(input: $input) {
      ruleId
      competitionYear
      name
      type
      value
      appliesTo
      targetFeeId
      targetStatus
      eligibility
      eligibilityCondition
      code
      maxUses
      currentUses
      expiresAt
      active
      createdAt
    }
  }
`;

export const updateDiscountRule = /* GraphQL */ `
  mutation UpdateDiscountRule($input: UpdateDiscountRuleInput!) {
    updateDiscountRule(input: $input) {
      ruleId
      competitionYear
      name
      type
      value
      appliesTo
      targetFeeId
      targetStatus
      eligibility
      eligibilityCondition
      code
      maxUses
      currentUses
      expiresAt
      active
      createdAt
    }
  }
`;

export const createPaymentIntent = /* GraphQL */ `
  mutation CreatePaymentIntent($input: CreatePaymentIntentInput!) {
    createPaymentIntent(input: $input) {
      clientSecret
      paymentIntentId
    }
  }
`;

export const snapshotFees = /* GraphQL */ `
  mutation SnapshotFees($applicantId: ID!, $competitionYear: Int!, $statusTrigger: FeeStatusTrigger!) {
    snapshotFees(applicantId: $applicantId, competitionYear: $competitionYear, statusTrigger: $statusTrigger) {
      applicantId
      competitionYear
      feeId
      feeName
      amount
      originalAmount
      statusTrigger
      required
      status
      discountRuleId
      discountName
      createdAt
    }
  }
`;

export const validateDiscountCode = /* GraphQL */ `
  mutation ValidateDiscountCode($input: ValidateDiscountCodeInput!) {
    validateDiscountCode(input: $input) {
      valid
      reason
      discountAmount
      discountName
      ruleId
      appliedToFeeIds
    }
  }
`;

export const createRefund = /* GraphQL */ `
  mutation CreateRefund($input: CreateRefundInput!) {
    createRefund(input: $input) {
      success
      message
    }
  }
`;

export const sendPaymentReminder = /* GraphQL */ `
  mutation SendPaymentReminder($applicantIds: [ID!]!, $competitionYear: Int!) {
    sendPaymentReminder(applicantIds: $applicantIds, competitionYear: $competitionYear) {
      success
      message
    }
  }
`;

// Phyllo Instagram linking mutations
// Requirements: 4.1, 4.2, 4.3, 4.4
export const initiatePhylloLink = /* GraphQL */ `
  mutation InitiatePhylloLink {
    initiatePhylloLink {
      success
      sdkToken
      phylloUserId
      message
    }
  }
`;

export const savePhylloConnection = /* GraphQL */ `
  mutation SavePhylloConnection($input: SavePhylloConnectionInput!) {
    savePhylloConnection(input: $input) {
      success
      instagramUsername
      message
    }
  }
`;

// ========================================
// Season-Scoped Instagram Insights Mutations
// Requirements: 1.5
// ========================================

export const setInstagramLinkingExempt = /* GraphQL */ `
  mutation SetInstagramLinkingExempt(
    $applicantId: ID!
    $competitionYear: Int!
    $exempt: Boolean!
  ) {
    setInstagramLinkingExempt(
      applicantId: $applicantId
      competitionYear: $competitionYear
      exempt: $exempt
    ) {
      applicantId
      competitionYear
      instagramLinkingExempt
      updatedAt
    }
  }
`;

// ========================================
// Stream Chat — Conversation Initiation Mutations
// Requirements: 11.3 (stream-chat-conversation-initiation)
// ========================================

export const CREATE_TEAM_CHANNEL = /* GraphQL */ `
  mutation CreateTeamChannel($input: CreateTeamChannelInput!) {
    createTeamChannel(input: $input) {
      channelCid
      name
      description
      visibility
      autoIncludeGroups
      createdByRole
      memberCount
      backfillWarnings
    }
  }
`;

export const ADD_CHANNEL_MEMBERS = /* GraphQL */ `
  mutation AddChannelMembers($channelCid: String!, $cognitoSubs: [String!]!) {
    addChannelMembers(channelCid: $channelCid, cognitoSubs: $cognitoSubs) {
      channelCid
      memberCount
    }
  }
`;

export const REMOVE_CHANNEL_MEMBERS = /* GraphQL */ `
  mutation RemoveChannelMembers($channelCid: String!, $cognitoSubs: [String!]!) {
    removeChannelMembers(channelCid: $channelCid, cognitoSubs: $cognitoSubs) {
      channelCid
      memberCount
    }
  }
`;

export const ADMIN_DELETE_CHANNEL = /* GraphQL */ `
  mutation AdminDeleteChannel($channelCid: String!) {
    adminDeleteChannel(channelCid: $channelCid) {
      channelCid
      deleted
    }
  }
`;

export const ADMIN_ADD_CHANNEL_MEMBERS = /* GraphQL */ `
  mutation AdminAddChannelMembers($channelCid: String!, $cognitoSubs: [String!]!) {
    adminAddChannelMembers(channelCid: $channelCid, cognitoSubs: $cognitoSubs) {
      channelCid
      memberCount
    }
  }
`;

export const ADMIN_REMOVE_CHANNEL_MEMBERS = /* GraphQL */ `
  mutation AdminRemoveChannelMembers($channelCid: String!, $cognitoSubs: [String!]!) {
    adminRemoveChannelMembers(channelCid: $channelCid, cognitoSubs: $cognitoSubs) {
      channelCid
      memberCount
    }
  }
`;

export const LEAVE_CHANNEL = /* GraphQL */ `
  mutation LeaveChannel($channelCid: String!) {
    leaveChannel(channelCid: $channelCid) {
      channelCid
      left
    }
  }
`;

// ========================================
// Stream Chat — Hide Conversations
// Requirements: stream-chat-hide-conversations R4
// ========================================
//
// Replaces the retired SOFT_DELETE_CONVERSATION, REJOIN_CONVERSATION,
// and CREATE_FRESH_START_DM mutations. The hide / show actions run
// from the browser via Stream's native channel.hide() / channel.show()
// — no AppSync mutation needed (Requirement 7.1). Only the create-
// side requires a server-side surface (Stream's user-role JWT cannot
// persist arbitrary members on create — Mrs Smith bug).

export const CREATE_DIRECT_MESSAGE = /* GraphQL */ `
  mutation CreateDirectMessage($targetCognitoSubs: [String!]!) {
    createDirectMessage(targetCognitoSubs: $targetCognitoSubs) {
      channelCid
      created
    }
  }
`;

// ========================================
// Avatar Crop — Director Mutation (avatar-crop spec)
// Requirements: 5.1, 6.5, 11.3
// ========================================

export const cropDirectorProfilePhoto = /* GraphQL */ `
  mutation CropDirectorProfilePhoto($input: CropDirectorProfilePhotoInput!) {
    cropDirectorProfilePhoto(input: $input) {
      croppedPhotoS3Key
    }
  }
`;


// ========================================
// Pageant Year Mutations (messaging-season-scoping)
// Requirements: 3.3, 8.6
// ========================================

export const createPageantYear = /* GraphQL */ `
  mutation CreatePageantYear($input: CreatePageantYearInput!) {
    createPageantYear(input: $input) {
      competitionYear
      displayName
      status
      archiveAt
      launchedAt
      createdAt
      updatedAt
    }
  }
`;

// ========================================
// Messaging Sub-Navigation (spec 2)
// ========================================

export const CREATE_BOOKMARK = /* GraphQL */ `
  mutation CreateBookmark($input: CreateBookmarkInput!) {
    createBookmark(input: $input) {
      bookmarkKey
      cognitoSub
      channelCid
      messageId
      createdAt
      competitionYear
      messageSnapshot {
        authorCognitoSub
        authorName
        text
        createdAt
        attachmentCount
      }
    }
  }
`;

export const DELETE_BOOKMARK = /* GraphQL */ `
  mutation DeleteBookmark($input: DeleteBookmarkInput!) {
    deleteBookmark(input: $input)
  }
`;

export const PIN_MESSAGE = /* GraphQL */ `
  mutation PinMessage($input: PinMessageInput!) {
    pinMessage(input: $input) {
      success
      pinned
      pinnedAt
      pinnedByCognitoSub
    }
  }
`;

export const DELETE_CHANNEL_ATTACHMENT = /* GraphQL */ `
  mutation DeleteChannelAttachment($input: DeleteChannelAttachmentInput!) {
    deleteChannelAttachment(input: $input) {
      success
      partialDelete
    }
  }
`;

// ---------------------------------------------------------------------------
// Messaging Activity + Drafts (spec 3)

export const MARK_ACTIVITY_READ = /* GraphQL */ `
  mutation MarkActivityRead($input: MarkActivityReadInput!) {
    markActivityRead(input: $input)
  }
`;

export const UPSERT_DRAFT = /* GraphQL */ `
  mutation UpsertDraft($input: UpsertDraftInput!) {
    upsertDraft(input: $input) {
      cognitoSub
      channelCid
      channelDisplayName
      bodyMarkdown
      pendingAttachments {
        tempId
        filename
        sizeBytes
        mimeType
        url
        status
        error
      }
      mentionedUserIds
      competitionYear
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_DRAFT = /* GraphQL */ `
  mutation DeleteDraft($input: DeleteDraftInput!) {
    deleteDraft(input: $input)
  }
`;

// ========================================
// Herald Feed Mutations (herald-feed spec)
// Community bulletin feed — all signed-in only.
// Requirements: 2.1
// ========================================

// Shared HeraldPost selection set, reused across the mutations that return a post.
const HERALD_POST_FIELDS = /* GraphQL */ `
  id
  authorCognitoSub
  authorName
  authorRole
  authorPhotoS3Key
  text
  imageS3Keys
  link {
    url
    title
    domain
    thumbnailS3Key
  }
  file {
    s3Key
    fileName
    contentType
    sizeBytes
  }
  taggedUsers {
    cognitoSub
    name
    photoS3Key
  }
  pinned
  likeCount
  liked
  bookmarked
  createdAt
  editedAt
`;

export const getHeraldImageUploadUrl = /* GraphQL */ `
  mutation GetHeraldImageUploadUrl($contentType: String!) {
    getHeraldImageUploadUrl(contentType: $contentType) {
      uploadUrl
      s3Key
      expiresIn
    }
  }
`;

export const getHeraldFileUploadUrl = /* GraphQL */ `
  mutation GetHeraldFileUploadUrl($contentType: String!, $sizeBytes: Int!) {
    getHeraldFileUploadUrl(contentType: $contentType, sizeBytes: $sizeBytes) {
      uploadUrl
      s3Key
      expiresIn
    }
  }
`;

export const createHeraldPost = /* GraphQL */ `
  mutation CreateHeraldPost($input: CreateHeraldPostInput!) {
    createHeraldPost(input: $input) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

export const editHeraldPost = /* GraphQL */ `
  mutation EditHeraldPost($id: ID!, $input: EditHeraldPostInput!) {
    editHeraldPost(id: $id, input: $input) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

export const pinHeraldPost = /* GraphQL */ `
  mutation PinHeraldPost($id: ID!) {
    pinHeraldPost(id: $id) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

export const unpinHeraldPost = /* GraphQL */ `
  mutation UnpinHeraldPost($id: ID!) {
    unpinHeraldPost(id: $id) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

export const deleteHeraldPost = /* GraphQL */ `
  mutation DeleteHeraldPost($id: ID!) {
    deleteHeraldPost(id: $id)
  }
`;

export const likeHeraldPost = /* GraphQL */ `
  mutation LikeHeraldPost($id: ID!) {
    likeHeraldPost(id: $id) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

export const unlikeHeraldPost = /* GraphQL */ `
  mutation UnlikeHeraldPost($id: ID!) {
    unlikeHeraldPost(id: $id) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

export const bookmarkHeraldPost = /* GraphQL */ `
  mutation BookmarkHeraldPost($id: ID!) {
    bookmarkHeraldPost(id: $id) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

export const unbookmarkHeraldPost = /* GraphQL */ `
  mutation UnbookmarkHeraldPost($id: ID!) {
    unbookmarkHeraldPost(id: $id) {
      ${HERALD_POST_FIELDS}
    }
  }
`;

// ========================================
// Division Registry mutations (division-registry spec, task 11.1)
//
// Director-managed, runtime-configurable Divisions per competitionYear. All
// four mutations are Admin-gated (@aws_cognito_user_pools only) in the schema;
// the frontend calls them with the default userPool auth. Each selects the full
// Division field set on the return so the management UI can update its local
// state from the resolver response without a refetch. Requirements: 1.1, 1.3,
// 1.4, 1.5, 1.6, 8.1.
// ========================================

const DIVISION_FIELDS = /* GraphQL */ `
  id
  competitionYear
  key
  label
  titlePrefix
  displayOrder
  appliesToApplications
  appliesToJudging
  active
  createdAt
  updatedAt
`;

export const createDivision = /* GraphQL */ `
  mutation CreateDivision($input: CreateDivisionInput!) {
    createDivision(input: $input) {
      ${DIVISION_FIELDS}
    }
  }
`;

// `titlePrefix` (title-approval-tracking spec) is an optional, additive arg:
// omitted ⇒ leave the prefix untouched; a value ⇒ set it; "" ⇒ clear it.
export const renameDivision = /* GraphQL */ `
  mutation RenameDivision($competitionYear: Int!, $key: String!, $label: String!, $titlePrefix: String) {
    renameDivision(competitionYear: $competitionYear, key: $key, label: $label, titlePrefix: $titlePrefix) {
      ${DIVISION_FIELDS}
    }
  }
`;

export const setDivisionActive = /* GraphQL */ `
  mutation SetDivisionActive($competitionYear: Int!, $key: String!, $active: Boolean!) {
    setDivisionActive(competitionYear: $competitionYear, key: $key, active: $active) {
      ${DIVISION_FIELDS}
    }
  }
`;

export const reorderDivisions = /* GraphQL */ `
  mutation ReorderDivisions($competitionYear: Int!, $orderedKeys: [String!]!) {
    reorderDivisions(competitionYear: $competitionYear, orderedKeys: $orderedKeys) {
      ${DIVISION_FIELDS}
    }
  }
`;

// ========================================
// Contestant-number assignment mutations (division-registry spec, task 11.2)
//
// Per-division, year-stable contestant numbers. Both mutations are Admin-gated
// (@aws_cognito_user_pools only) in the schema. `assignContestantNumber` with
// no `number` in the input auto-sequences the lowest unused positive integer;
// providing `number` is a manual override. `reassignContestantNumber` moves a
// contestant to a new number, releasing the old one for reuse. Each returns the
// resulting assignment so the UI can update its local state without a refetch.
// Requirements: 4.1, 4.7, 4.8, 8.3.
// ========================================

const CONTESTANT_NUMBER_FIELDS = /* GraphQL */ `
  competitionYear
  divisionKey
  personId
  number
  assignedAt
`;

export const assignContestantNumber = /* GraphQL */ `
  mutation AssignContestantNumber($input: AssignContestantNumberInput!) {
    assignContestantNumber(input: $input) {
      ${CONTESTANT_NUMBER_FIELDS}
    }
  }
`;

export const reassignContestantNumber = /* GraphQL */ `
  mutation ReassignContestantNumber($input: AssignContestantNumberInput!) {
    reassignContestantNumber(input: $input) {
      ${CONTESTANT_NUMBER_FIELDS}
    }
  }
`;

// Drag-to-order renumber: rewrite a division's numbers 1..N from an arranged
// order in one pass (no transient uniqueness collisions). Per-division scope.
export const reorderContestantNumbers = /* GraphQL */ `
  mutation ReorderContestantNumbers($input: ReorderContestantNumbersInput!) {
    reorderContestantNumbers(input: $input) {
      ${CONTESTANT_NUMBER_FIELDS}
    }
  }
`;

// ========================================
// Season-Scoped Title Catalog mutations (season-scoped-title-registry spec,
// task 8)
//
// Director-managed, runtime-configurable Titles per competitionYear, mirroring
// the Division mutations. ALL five mutations are Admin-gated
// (@aws_cognito_user_pools only) in the schema; the frontend calls them with
// the default userPool auth. Each selects the full Title field set on the
// return so the management UI can update its local state from the resolver
// response without a refetch. Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 3.1.
// ========================================

const TITLE_FIELDS = /* GraphQL */ `
  id
  competitionYear
  key
  label
  active
  displayOrder
  applicantType
  divisionKey
  createdAt
  updatedAt
`;

export const createTitle = /* GraphQL */ `
  mutation CreateTitle($input: CreateTitleInput!) {
    createTitle(input: $input) {
      ${TITLE_FIELDS}
    }
  }
`;

export const renameTitle = /* GraphQL */ `
  mutation RenameTitle($competitionYear: Int!, $key: String!, $label: String!) {
    renameTitle(competitionYear: $competitionYear, key: $key, label: $label) {
      ${TITLE_FIELDS}
    }
  }
`;

export const setTitleActive = /* GraphQL */ `
  mutation SetTitleActive($competitionYear: Int!, $key: String!, $active: Boolean!) {
    setTitleActive(competitionYear: $competitionYear, key: $key, active: $active) {
      ${TITLE_FIELDS}
    }
  }
`;

export const reorderTitles = /* GraphQL */ `
  mutation ReorderTitles($competitionYear: Int!, $orderedKeys: [String!]!) {
    reorderTitles(competitionYear: $competitionYear, orderedKeys: $orderedKeys) {
      ${TITLE_FIELDS}
    }
  }
`;

export const cloneTitlesFromPreviousSeason = /* GraphQL */ `
  mutation CloneTitlesFromPreviousSeason($targetCompetitionYear: Int!) {
    cloneTitlesFromPreviousSeason(targetCompetitionYear: $targetCompetitionYear) {
      ${TITLE_FIELDS}
    }
  }
`;

// ---------------------------------------------------------------------------
// Role assignments (community-contacts Roles & Access inline editor).
// Presence of these exports is what the capability probe
// (`writeMutationsAvailable`) checks to un-gate the inline Grant/End affordances
// on the Person detail page. The resolvers are deployed on the AppSync API
// (createRoleAssignment / endRoleAssignment).
// ---------------------------------------------------------------------------

export const createRoleAssignment = /* GraphQL */ `
  mutation CreateRoleAssignment($input: CreateRoleAssignmentInput!) {
    createRoleAssignment(input: $input) {
      id
      subjectKey
      role
      grantedAt
      endedAt
      scope
      active
    }
  }
`;

export const endRoleAssignment = /* GraphQL */ `
  mutation EndRoleAssignment($id: ID!) {
    endRoleAssignment(id: $id) {
      id
      subjectKey
      role
      grantedAt
      endedAt
      scope
      active
    }
  }
`;

// ========================================
// Director Document Library Mutations
// (director-document-library spec)
//
// Upload presign reuses the existing PresignedUrlResponse; create/replace are
// confirm-after-upload (the server re-validates the actual stored bytes before
// persisting). Edit recomputes the audience; remove is a soft delete that is
// not reversible through the Document_Center. Requirements: 4.x, 5.x, 6.x
// ========================================

// Upload presign for create/replace — identity-scoped, ≤15 min, admin +
// administersAnchor + declared type/size re-validation (R4.4, R4.8, R5.6, R6.4).
export const getLibraryDocumentUploadUrl = /* GraphQL */ `
  mutation GetLibraryDocumentUploadUrl(
    $contentType: String!
    $sizeBytes: Int!
    $anchorType: ScopeAnchorType!
    $anchorId: ID!
  ) {
    getLibraryDocumentUploadUrl(
      contentType: $contentType
      sizeBytes: $sizeBytes
      anchorType: $anchorType
      anchorId: $anchorId
    ) {
      uploadUrl
      s3Key
      expiresIn
    }
  }
`;

// Confirm-after-upload persist; server re-validates the actual stored bytes
// before writing (R4.5, R5.7).
export const createLibraryDocument = /* GraphQL */ `
  mutation CreateLibraryDocument(
    $title: String!
    $scope: DocumentScopeInput!
    $s3Key: String!
    $fileName: String!
    $contentType: String!
    $sizeBytes: Int!
  ) {
    createLibraryDocument(
      title: $title
      scope: $scope
      s3Key: $s3Key
      fileName: $fileName
      contentType: $contentType
      sizeBytes: $sizeBytes
    ) {
      id
      title
      scope {
        anchorType
        anchorId
        competitionYear
        divisionKeys
        applicantTypes
      }
      fileName
      contentType
      sizeBytes
      uploadedAt
      updatedAt
      version
      fileAvailable
    }
  }
`;

// Replace the file, preserving title/scope unless changed (R6.2, R6.4, R6.7).
export const replaceLibraryDocumentFile = /* GraphQL */ `
  mutation ReplaceLibraryDocumentFile(
    $id: ID!
    $version: Int!
    $s3Key: String!
    $fileName: String!
    $contentType: String!
    $sizeBytes: Int!
  ) {
    replaceLibraryDocumentFile(
      id: $id
      version: $version
      s3Key: $s3Key
      fileName: $fileName
      contentType: $contentType
      sizeBytes: $sizeBytes
    ) {
      id
      title
      scope {
        anchorType
        anchorId
        competitionYear
        divisionKeys
        applicantTypes
      }
      fileName
      contentType
      sizeBytes
      uploadedAt
      updatedAt
      version
      fileAvailable
    }
  }
`;

// Edit title and/or Document_Scope; recomputes the audience (R6.2, R6.6, R6.8).
export const editLibraryDocument = /* GraphQL */ `
  mutation EditLibraryDocument($id: ID!, $version: Int!, $title: String, $scope: DocumentScopeInput!) {
    editLibraryDocument(id: $id, version: $version, title: $title, scope: $scope) {
      id
      title
      scope {
        anchorType
        anchorId
        competitionYear
        divisionKeys
        applicantTypes
      }
      fileName
      contentType
      sizeBytes
      uploadedAt
      updatedAt
      version
      fileAvailable
    }
  }
`;

// Soft delete; not reversible via the Document_Center (R6.3, R6.7).
export const removeLibraryDocument = /* GraphQL */ `
  mutation RemoveLibraryDocument($id: ID!, $version: Int!) {
    removeLibraryDocument(id: $id, version: $version)
  }
`;

// ---------------------------------------------------------------------------
// Tenant foundation (tenant-foundation Phase 1). All DARK until enforcement
// flips (Task 10); provisionTenant is PlatformAdmins-gated in the resolver.
// ---------------------------------------------------------------------------

export const provisionTenant = /* GraphQL */ `
  mutation ProvisionTenant($tenantId: ID!, $name: String!) {
    provisionTenant(tenantId: $tenantId, name: $name) {
      success
      created
      tenantId
      message
    }
  }
`;

export const joinTenant = /* GraphQL */ `
  mutation JoinTenant($tenantId: ID!, $role: String) {
    joinTenant(tenantId: $tenantId, role: $role) {
      success
      joined
      created
      tenantId
      roles
      status
      message
    }
  }
`;

/**
 * Enroll a minor under the signed-in adult as guardian (tenant-foundation Task 16).
 *
 * NOTE what is NOT sent: no guardian id (the server uses the token sub), no age
 * band, and no consent state. The birthdate goes up and the SERVER decides
 * whether this subject is a COPPA child, whether the tenant may enroll one at
 * all, and what consent state the membership starts in. `refusal.reason` is a
 * stable code — map it to copy, don't parse `message`.
 */
export const enrollMinorUnderGuardian = /* GraphQL */ `
  mutation EnrollMinorUnderGuardian(
    $tenantId: ID!
    $minorLegalFirstName: String!
    $minorLegalLastName: String!
    $minorPreferredName: String
    $minorDateOfBirth: AWSDate!
    $requestedRole: String
  ) {
    enrollMinorUnderGuardian(
      tenantId: $tenantId
      minorLegalFirstName: $minorLegalFirstName
      minorLegalLastName: $minorLegalLastName
      minorPreferredName: $minorPreferredName
      minorDateOfBirth: $minorDateOfBirth
      requestedRole: $requestedRole
    ) {
      success
      enrolled
      created
      minorPersonId
      guardianPersonId
      tenantId
      band
      requiresConsent
      consentState
      usingPlaceholderPolicy
      refusal {
        reason
        missingPolicyFields
      }
      message
    }
  }
`;

/**
 * Request a short-lived upload URL for the SIGNED parental-consent form
 * (tenant-foundation Req 11.5).
 *
 * The client does NOT choose the object key — `s3Key` comes back from the server
 * and must be passed through to `recordConsent` verbatim. Requesting an upload is
 * not consent; the membership stays PENDING.
 */
export const requestConsentArtifactUpload = /* GraphQL */ `
  mutation RequestConsentArtifactUpload(
    $tenantId: ID!
    $minorPersonId: ID!
    $contentType: String!
    $sizeBytes: Int!
  ) {
    requestConsentArtifactUpload(
      tenantId: $tenantId
      minorPersonId: $minorPersonId
      contentType: $contentType
      sizeBytes: $sizeBytes
    ) {
      success
      uploadUrl
      s3Key
      expiresIn
      refusalReason
      message
    }
  }
`;

/**
 * Record one parental-consent transition (tenant-foundation Req 7/9/11.5).
 *
 * `signedFormDocumentRef` must be the `s3Key` returned by
 * `requestConsentArtifactUpload` — the server re-validates that it belongs to this
 * exact (tenant, minor) AND that the object actually exists before it will GRANT,
 * so a fabricated value cannot produce a granted consent. The VPC method and
 * notice version are read server-side from the tenant's config, never sent.
 */
export const recordConsent = /* GraphQL */ `
  mutation RecordConsent(
    $tenantId: ID!
    $minorPersonId: ID!
    $event: ConsentEvent!
    $signedFormDocumentRef: String
    $scope: String
  ) {
    recordConsent(
      tenantId: $tenantId
      minorPersonId: $minorPersonId
      event: $event
      signedFormDocumentRef: $signedFormDocumentRef
      scope: $scope
    ) {
      success
      priorState
      newState
      message
    }
  }
`;

export const switchActiveTenant = /* GraphQL */ `
  mutation SwitchActiveTenant($tenantId: ID!) {
    switchActiveTenant(tenantId: $tenantId) {
      success
      activeTenantId
      membershipStatus
      message
    }
  }
`;

// setTenantStatus — the PlatformAdmins-gated operator suspend/reactivate. ONE
// setter covers both directions (status: active | suspended). PlatformAdmins is
// enforced in the resolver; the UI gate is defense-in-depth.
export const setTenantStatus = /* GraphQL */ `
  mutation SetTenantStatus($tenantId: ID!, $status: TenantStatus!) {
    setTenantStatus(tenantId: $tenantId, status: $status) {
      success
      tenantId
      status
      message
    }
  }
`;

/**
 * tenant-foundation (Task 7.1 / Req 15.6) — open a time-boxed, READ-ONLY support
 * window into a tenant for a platform operator. PLATFORM-operator only; the
 * resolver carries the same fail-closed PlatformAdmins guard as `provisionTenant`.
 *
 * `minutes` is optional and CLAMPED server-side to the 60-minute maximum, so an
 * over-long request degrades safely instead of being refused. `expiresAt` in the
 * result reflects the window actually granted, not the one requested.
 */
export const grantSupportAccess = /* GraphQL */ `
  mutation GrantSupportAccess(
    $tenantId: ID!
    $operatorPersonId: ID!
    $reason: String!
    $minutes: Int
  ) {
    grantSupportAccess(
      tenantId: $tenantId
      operatorPersonId: $operatorPersonId
      reason: $reason
      minutes: $minutes
    ) {
      success
      tenantId
      operatorPersonId
      expiresAt
      message
    }
  }
`;

/**
 * tenant-foundation (Task 7.1 / Req 15.6) — end a support window immediately.
 * Idempotent: revoking an absent or already-revoked grant succeeds with
 * `revoked: false`, because the caller's intent (that the access not exist) holds
 * either way. The row is stamped, never deleted.
 */
export const revokeSupportAccess = /* GraphQL */ `
  mutation RevokeSupportAccess($tenantId: ID!, $operatorPersonId: ID!) {
    revokeSupportAccess(tenantId: $tenantId, operatorPersonId: $operatorPersonId) {
      success
      tenantId
      operatorPersonId
      revoked
      message
    }
  }
`;
